import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Square, X, Keyboard, Send, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const FLAGS = { Togo: "🇹🇬", Gabon: "🇬🇦", France: "🇫🇷", Rwanda: "🇷🇼" };
const CORRIDOR_MAP = {
  "Togo|Gabon": "tg_ga",
  "Gabon|France": "ga_fr",
  "Gabon|Rwanda": "ga_rw",
  "France|Gabon": "fr_ga",
};
const WELCOME = "Bonjour ! Où souhaitez-vous envoyer votre argent aujourd'hui ?";

function ChatBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={`sl-va-msg flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-snug ${
          isUser
            ? "bg-[var(--sl-primary)] text-white rounded-br-sm"
            : "bg-gray-100 text-gray-800 rounded-bl-sm"
        }`}
        data-testid={isUser ? "va-message-user" : "va-message-assistant"}
      >
        {text}
      </div>
    </div>
  );
}

function TransferPreview({ intent, onContinue }) {
  const amountFcfa = intent.currency === "EUR" ? Math.round(intent.amount * 656) : Math.round(intent.amount);
  return (
    <div className="rounded-xl border border-[var(--sl-primary)]/25 bg-[var(--sl-bg-alt)]/60 p-4" data-testid="va-transfer-preview">
      <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Aperçu de votre transfert</div>
      <div className="mt-2 flex items-center gap-2 text-lg font-heading font-bold">
        <span>{FLAGS[intent.origin]} {intent.origin}</span>
        <ArrowRight size={16} className="text-gray-400" />
        <span>{FLAGS[intent.destination]} {intent.destination}</span>
      </div>
      <div className="mt-1 text-2xl font-heading font-bold text-[var(--sl-primary)]">
        {amountFcfa.toLocaleString("fr-FR")} FCFA
      </div>
      <button
        onClick={() => onContinue(intent, amountFcfa)}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-[var(--sl-primary)] hover:bg-[var(--sl-primary-light)] transition-colors"
        data-testid="va-continue-btn"
      >
        Continuer vers le transfert <ArrowRight size={15} />
      </button>
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-500">
        <CheckCircle2 size={12} /> Vous vérifiez toutes les informations avant confirmation.
      </div>
    </div>
  );
}

export default function VoiceAssistant() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | listening | transcribing | thinking
  const [showTextInput, setShowTextInput] = useState(false);
  const [textValue, setTextValue] = useState("");
  const [lastIntent, setLastIntent] = useState(null);

  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = (crypto?.randomUUID?.() || `va-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  }
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const isActiveRef = useRef(true);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: "assistant", text: WELCOME }]);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, status]);

  // Stop any in-flight recording/playback if the widget unmounts (e.g. navigation away).
  useEffect(() => {
    return () => {
      isActiveRef.current = false;
      // Detach onstop FIRST so a recording in progress doesn't trigger the
      // transcribe → chat → speak pipeline after the component is gone.
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.onstop = null;
        try { if (mediaRecorderRef.current.state === "recording") mediaRecorderRef.current.stop(); } catch (_e) { /* ignore */ }
      }
      try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch (_e) { /* ignore */ }
      try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
    };
  }, []);

  const sendToAssistant = useCallback(async (userText) => {
    setMessages((prev) => [...prev, { role: "user", text: userText }]);
    setStatus("thinking");
    try {
      const { data } = await api.post("/voice-assistant/chat", {
        session_id: sessionIdRef.current,
        message: userText,
      });
      if (!isActiveRef.current) return;
      setMessages((prev) => [...prev, { role: "assistant", text: data.reply }]);
      if (data.ready && data.origin && data.destination && data.amount) {
        setLastIntent(data);
      } else {
        setLastIntent(null);
      }
      setStatus("idle");
    } catch (_e) {
      if (isActiveRef.current) {
        toast.error("L'assistant est momentanément indisponible. Réessayez.");
        setStatus("idle");
      }
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      // Capture mono 16kHz — Whisper resamples to 16kHz internally anyway, so recording at
      // that rate (instead of the default 44.1/48kHz stereo) roughly halves the upload payload
      // without any loss of transcription quality, which cuts the "transcribing" wait noticeably
      // on slower mobile connections.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      // Low bitrate is plenty for speech and keeps the recorded blob small (faster upload).
      const recorderOpts = { audioBitsPerSecond: 24000, ...(mime ? { mimeType: mime } : {}) };
      const recorder = new MediaRecorder(stream, recorderOpts);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 500) { setStatus("idle"); return; }
        setStatus("transcribing");
        try {
          const form = new FormData();
          form.append("audio", blob, "recording.webm");
          const { data } = await api.post("/voice-assistant/transcribe", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (!isActiveRef.current) return;
          const text = (data.text || "").trim();
          if (!text) {
            toast.error("Je n'ai rien entendu, pouvez-vous réessayer ?");
            setStatus("idle");
            return;
          }
          await sendToAssistant(text);
        } catch (_e) {
          if (isActiveRef.current) {
            toast.error("Échec de la transcription. Réessayez ou utilisez le clavier.");
            setStatus("idle");
          }
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setStatus("listening");
    } catch (_e) {
      toast.error("Microphone non autorisé. Utilisez le clavier ci-dessous.");
      setShowTextInput(true);
    }
  }, [sendToAssistant]);

  // cancel=true aborts the recording silently (no transcription pipeline) — used when the
  // panel is closed mid-recording. cancel=false is the normal "stop & process" path.
  const stopRecording = useCallback((cancel = false) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (cancel) {
      recorder.onstop = null;
      try { if (recorder.state === "recording") recorder.stop(); } catch (_e) { /* ignore */ }
      try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch (_e) { /* ignore */ }
      streamRef.current = null;
      setStatus("idle");
    } else {
      try { recorder.stop(); } catch (_e) { /* ignore */ }
    }
  }, []);

  const handleMicClick = () => {
    if (status === "listening") stopRecording();
    else if (status === "idle") startRecording();
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    const text = textValue.trim();
    if (!text || status !== "idle") return;
    setTextValue("");
    sendToAssistant(text);
  };

  const handleContinue = (intent, amountFcfa) => {
    audioRef.current?.pause();
    const corridor = CORRIDOR_MAP[`${intent.origin}|${intent.destination}`];
    navigate(`/envoyer?corridor=${corridor}&amount=${amountFcfa}`);
  };

  const handleClose = () => {
    if (status === "listening") stopRecording(true);
    audioRef.current?.pause();
    setIsOpen(false);
  };

  const busy = status !== "idle" && status !== "listening";

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="sl-va-fab-pulse fixed bottom-44 md:bottom-20 right-4 z-[60] w-12 h-12 rounded-full bg-[var(--sl-primary)] text-white grid place-items-center shadow-[0_10px_30px_-6px_rgba(255,106,0,0.6)] hover:scale-105 transition-transform"
          data-testid="voice-assistant-fab"
          aria-label="Ouvrir l'assistant vocal"
        >
          <Mic size={20} />
        </button>
      )}

      {isOpen && (
        <div
          className="sl-va-panel fixed bottom-20 md:bottom-4 right-4 left-4 md:left-auto md:w-[380px] z-[60] rounded-2xl bg-white shadow-[0_24px_70px_-12px_rgba(0,0,0,0.3)] border border-gray-200 flex flex-col overflow-hidden"
          style={{ maxHeight: "min(600px, 70vh)" }}
          data-testid="voice-assistant-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[var(--sl-primary)]/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--sl-primary)] text-white grid place-items-center flex-shrink-0">
                <Mic size={18} />
              </div>
              <div>
                <div className="font-heading font-bold text-sm text-gray-900">Parler à SwiftLink</div>
                <div className="text-[11px] text-gray-500">Votre assistant de transfert IA</div>
                <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En ligne
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500 flex-shrink-0"
              data-testid="voice-assistant-close-btn"
              aria-label="Fermer l'assistant"
            >
              <X size={16} />
            </button>
          </div>

          {/* Chat */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[200px]">
            {messages.map((m, i) => (
              <ChatBubble key={i} role={m.role} text={m.text} />
            ))}
            {lastIntent && <TransferPreview intent={lastIntent} onContinue={handleContinue} />}
            {status === "thinking" && (
              <div className="flex items-center gap-1.5 px-1" data-testid="va-thinking-indicator">
                <span className="sl-va-dot" style={{ animationDelay: "0ms" }} />
                <span className="sl-va-dot" style={{ animationDelay: "150ms" }} />
                <span className="sl-va-dot" style={{ animationDelay: "300ms" }} />
              </div>
            )}
          </div>

          {/* Footer / input */}
          <div className="border-t border-gray-100 px-4 py-3">
            {status === "listening" && (
              <div className="flex items-center justify-center gap-2 mb-2 text-xs text-gray-500" data-testid="va-listening-indicator">
                <div className="flex items-end gap-0.5 h-4">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className="sl-va-bar h-full" style={{ animationDelay: `${i * 0.12}s` }} />
                  ))}
                </div>
                Je vous écoute... parlez maintenant
              </div>
            )}
            {busy && status !== "listening" && (
              <div className="text-center text-xs text-gray-400 mb-2">
                {status === "transcribing" && "Transcription en cours..."}
                {status === "thinking" && "L'assistant réfléchit..."}
              </div>
            )}

            {showTextInput ? (
              <form onSubmit={handleTextSubmit} className="flex items-center gap-2">
                <input
                  type="text"
                  value={textValue}
                  onChange={(e) => setTextValue(e.target.value)}
                  placeholder="Écrivez votre message..."
                  disabled={busy}
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[var(--sl-primary)]"
                  data-testid="voice-assistant-text-input"
                />
                <button
                  type="submit"
                  disabled={busy || !textValue.trim()}
                  className="w-10 h-10 rounded-full bg-[var(--sl-primary)] text-white grid place-items-center disabled:opacity-40 flex-shrink-0"
                  data-testid="voice-assistant-send-btn"
                >
                  <Send size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowTextInput(false)}
                  className="w-10 h-10 rounded-full border border-gray-200 text-gray-500 grid place-items-center flex-shrink-0"
                  data-testid="voice-assistant-mic-toggle"
                  aria-label="Utiliser le micro"
                >
                  <Mic size={16} />
                </button>
              </form>
            ) : (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setShowTextInput(true)}
                  disabled={busy}
                  className="w-10 h-10 rounded-full border border-gray-200 text-gray-500 grid place-items-center hover:bg-gray-50 disabled:opacity-40"
                  data-testid="voice-assistant-keyboard-btn"
                  aria-label="Écrire au lieu de parler"
                >
                  <Keyboard size={16} />
                </button>
                <button
                  onClick={handleMicClick}
                  disabled={busy}
                  className={`w-14 h-14 rounded-full grid place-items-center text-white shadow-lg transition-colors disabled:opacity-50 ${
                    status === "listening" ? "bg-red-500" : "bg-[var(--sl-primary)] hover:bg-[var(--sl-primary-light)]"
                  }`}
                  data-testid="voice-assistant-mic-btn"
                  aria-label={status === "listening" ? "Arrêter l'enregistrement" : "Parler"}
                >
                  {status === "listening" ? <Square size={20} /> : <Mic size={22} />}
                </button>
                <div className="w-10" />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
